import {
    CLEAR_MARKETING_ADS,
    LOGOUT,
    SET_MARKETING_ADS_COMBINED,
    SET_MARKETING_ADS_FILTERS,
    SET_MARKETING_ADS_GOOGLE,
    SET_MARKETING_ADS_META,
} from '../actions/types';
import { MARKETING_ADS_MAX_ENTRIES } from '../../components/marketing/marketingAdsCacheKey';

/**
 * Multi-entry report cache.
 *
 * Before: each channel held ONE entry keyed by `cacheKey`, so visiting
 * range B evicted range A and going back to A refetched everything.
 * Now: `entries` is a map cacheKey -> report, capped at
 * MARKETING_ADS_MAX_ENTRIES (oldest evicted). The Both view has no
 * snapshot of its own — SET_MARKETING_ADS_COMBINED fans out into the
 * Meta (`|both`) and Google entries for that range, so a refresh or
 * sync on any tab is immediately visible on the other two.
 *
 * `targets` are date-independent and live at channel level.
 */
const emptyChannel = () => ({
    entries: {},
    targets: {},
});

const initialState = {
    filters: {
        from: '',
        to: '',
        metaPlatform: 'both',
        channel: 'both',
    },
    meta: emptyChannel(),
    google: emptyChannel(),
    // Kept only so persisted/legacy state has a home; new code reads
    // the Meta/Google entries instead of a combined snapshot.
    combined: emptyChannel(),
};

function upsertEntry(channel, payload) {
    const cacheKey = payload?.cacheKey;
    if (!cacheKey) return channel;
    const { targets, ...entryPayload } = payload;
    const entries = { ...(channel.entries || {}) };
    entries[cacheKey] = {
        ...(entries[cacheKey] || {}),
        ...entryPayload,
        cacheKey,
        lastUpdated: payload.lastUpdated ?? Date.now(),
    };
    // Evict least-recently-updated entries beyond the cap.
    const keys = Object.keys(entries).sort(
        (a, b) => (entries[a].lastUpdated || 0) - (entries[b].lastUpdated || 0),
    );
    while (keys.length > MARKETING_ADS_MAX_ENTRIES) {
        delete entries[keys.shift()];
    }
    return {
        entries,
        targets: targets ? { ...targets } : (channel.targets || {}),
    };
}

export default function marketingAdsReducer(state = initialState, action) {
    switch (action.type) {
        case SET_MARKETING_ADS_FILTERS:
            return {
                ...state,
                filters: { ...state.filters, ...action.payload },
            };
        case SET_MARKETING_ADS_META:
            return { ...state, meta: upsertEntry(state.meta || emptyChannel(), action.payload) };
        case SET_MARKETING_ADS_GOOGLE:
            return { ...state, google: upsertEntry(state.google || emptyChannel(), action.payload) };
        case SET_MARKETING_ADS_COMBINED: {
            // Fan out into the real channel caches (cacheKey is `from|to`).
            const { cacheKey, metaCampaigns, googleCampaigns, records, metaCityDaily } = action.payload || {};
            if (!cacheKey) return state;
            const [from, to] = String(cacheKey).split('|');
            if (!from || !to) return state;
            let next = state;
            if (metaCampaigns || metaCityDaily) {
                next = {
                    ...next,
                    meta: upsertEntry(next.meta || emptyChannel(), {
                        cacheKey: `${from}|${to}|both`,
                        ...(metaCampaigns ? { campaigns: metaCampaigns } : {}),
                        ...(metaCityDaily ? { metaCityDaily } : {}),
                    }),
                };
            }
            if (googleCampaigns || records) {
                next = {
                    ...next,
                    google: upsertEntry(next.google || emptyChannel(), {
                        cacheKey,
                        ...(googleCampaigns ? { campaigns: googleCampaigns } : {}),
                        ...(records ? { records } : {}),
                    }),
                };
            }
            return next;
        }
        case CLEAR_MARKETING_ADS:
        case LOGOUT:
            return initialState;
        default:
            return state;
    }
}
